import {
    Badge,
    BadgeColor,
    Button,
    ButtonColor,
    Container,
    ContainerSize,
    DataTable,
    DataTableColor,
    DataTableSize,
    Heading,
    Text,
    type DataTableColumn,
} from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type DataTablePageProps = {}

// Sample data types
type User = {
    id: number;
    name: string;
    email: string;
    role: string;
    status: 'active' | 'inactive';
};

const sampleUsers: User[] = [
    { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Admin', status: 'active' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'Editor', status: 'active' },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', role: 'Viewer', status: 'inactive' },
    { id: 4, name: 'Alice Williams', email: 'alice@example.com', role: 'Editor', status: 'active' },
];

const DataTablePage = ({ }: DataTablePageProps) => {

    // Basic columns
    const basicColumns: DataTableColumn<User>[] = [
        { header: 'ID', accessor: 'id' },
        { header: 'Name', accessor: 'name' },
        { header: 'Email', accessor: 'email' },
        { header: 'Role', accessor: 'role' },
    ];

    // Columns with custom rendering
    const customColumns: DataTableColumn<User>[] = [
        { header: 'ID', accessor: 'id', width: 80, align: 'center' },
        { header: 'Name', accessor: 'name' },
        { header: 'Email', accessor: 'email' },
        {
            header: 'Status',
            accessor: (row) => (
                <Badge color={row.status === 'active' ? BadgeColor.Success : BadgeColor.Secondary}>
                    {row.status}
                </Badge>
            ),
            align: 'center'
        },
        {
            header: 'Actions',
            accessor: () => (
                <Button color={ButtonColor.Primary} className="btn-sm">
                    Edit
                </Button>
            ),
            align: 'center'
        }
    ];

    // Aligned columns
    const alignedColumns: DataTableColumn<User>[] = [
        { header: 'ID', accessor: 'id', align: 'right' },
        { header: 'Name', accessor: 'name', align: 'left' },
        { header: 'Email', accessor: 'email', align: 'center' },
        { header: 'Role', accessor: 'role', align: 'right' },
    ];

    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Data table
            </Heading>
            <Text className="mb-4">
                The <Text el="strong">DataTable</Text> component provides a flexible and feature-rich way to display
                tabular data with support for styling, responsive layouts, custom rendering, and interactions.
            </Text>

            {/* Basic Usage */}
            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Basic Usage
            </Heading>
            <Text className="mb-4">
                Create a simple table by defining columns and passing data.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable 
                        columns={basicColumns}
                        data={sampleUsers}
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {basicTableCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Striped Table */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Striped Rows
            </Heading>
            <Text className="mb-4">
                Add zebra-striping to table rows with the <Text el="code" className="bg-light px-2 py-1 rounded">striped</Text> prop.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={basicColumns}
                        data={sampleUsers}
                        striped
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {stripedTableCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Striped Columns */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Striped Columns
            </Heading>
            <Text className="mb-4">
                Add zebra-striping to table columns with the <Text el="code" className="bg-light px-2 py-1 rounded">stripedColumns</Text> prop.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={basicColumns}
                        data={sampleUsers}
                        stripedColumns
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {stripedColumnsCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Bordered Table */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Bordered Table
            </Heading>
            <Text className="mb-4">
                Add borders on all sides of the table and cells.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={basicColumns}
                        data={sampleUsers}
                        bordered
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {borderedTableCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Borderless Table */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Borderless Table
            </Heading>
            <Text className="mb-4">
                Remove all borders from the table.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={basicColumns}
                        data={sampleUsers}
                        borderless
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {borderlessTableCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Hoverable Table */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Hoverable Rows
            </Heading>
            <Text className="mb-4">
                Enable a hover state on table rows.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={basicColumns}
                        data={sampleUsers}
                        hover
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {hoverTableCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Small Table */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Table Sizes
            </Heading>
            <Text className="mb-4">
                Make tables more compact with the <Text el="code" className="bg-light px-2 py-1 rounded">size</Text> prop.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={basicColumns}
                        data={sampleUsers}
                        size={DataTableSize.Small}
                        bordered
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {smallTableCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Dark Header */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Dark Header
            </Heading>
            <Text className="mb-4">
                Apply a dark background to the table header.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={basicColumns}
                        data={sampleUsers}
                        darkHeader
                        striped
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {darkHeaderTableCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Table colors */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Table color
            </Heading>
            <Text className="mb-4">
                Use the <Text el="code">color</Text> prop for Bootstrap contextual table classes.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <div className="mb-3">
                        <Text className="fw-bold mb-2">Primary</Text>
                        <DataTable
                            columns={basicColumns}
                            data={sampleUsers.slice(0, 2)}
                            color={DataTableColor.Primary}
                        />
                    </div>
                    <div className="mb-3">
                        <Text className="fw-bold mb-2">Success</Text>
                        <DataTable
                            columns={basicColumns}
                            data={sampleUsers.slice(0, 2)}
                            color={DataTableColor.Success}
                        />
                    </div>
                    <div className="mb-3">
                        <Text className="fw-bold mb-2">Dark</Text>
                        <DataTable
                            columns={basicColumns}
                            data={sampleUsers.slice(0, 2)}
                            color={DataTableColor.Dark}
                        />
                    </div>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {colorTableCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Column Alignment */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Column Alignment
            </Heading>
            <Text className="mb-4">
                Control text alignment for individual columns using the <Text el="code" className="bg-light px-2 py-1 rounded">align</Text> property.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={alignedColumns}
                        data={sampleUsers}
                        bordered
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {alignedTableCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Custom Rendering */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Custom Cell Rendering
            </Heading>
            <Text className="mb-4">
                Use custom accessor functions to render complex cell content like badges and buttons.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={customColumns}
                        data={sampleUsers}
                        hover
                        bordered
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {customRenderingCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Row Click Handler */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Clickable Rows
            </Heading>
            <Text className="mb-4">
                Make rows clickable with the <Text el="code" className="bg-light px-2 py-1 rounded">onRowClick</Text> handler.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={basicColumns}
                        data={sampleUsers}
                        hover
                        onRowClick={(row, index) => alert(`Clicked on ${row.name} (row ${index})`)}
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {clickableRowsCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Caption */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Table Caption
            </Heading>
            <Text className="mb-4">
                Add a caption to describe the table content.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={basicColumns}
                        data={sampleUsers}
                        caption="List of active users"
                        bordered
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {captionTableCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Caption Top */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Caption on Top
            </Heading>
            <Text className="mb-4">
                Place the caption above the table.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={basicColumns}
                        data={sampleUsers}
                        caption="List of active users"
                        captionTop
                        bordered
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {captionTopTableCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Empty State */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Empty State
            </Heading>
            <Text className="mb-4">
                Display a custom message when there's no data.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={basicColumns}
                        data={[]}
                        emptyMessage="No users found. Please add some users."
                        bordered
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {emptyStateCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Responsive Tables */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Responsive Tables
            </Heading>
            <Text className="mb-4">
                Tables are responsive by default. You can control responsiveness with breakpoints.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <DataTable
                        columns={basicColumns}
                        data={sampleUsers}
                        responsive="md"
                        bordered
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {responsiveTableCode}
                </CodeHighlighter>
            </CodeSnippet>

        </Container>
    )
}

export default DataTablePage;

// Code examples
const basicTableCode = `type User = {
  id: number;
  name: string;
  email: string;
  role: string;
};

const columns: DataTableColumn<User>[] = [
  { header: 'ID', accessor: 'id' },
  { header: 'Name', accessor: 'name' },
  { header: 'Email', accessor: 'email' },
  { header: 'Role', accessor: 'role' },
];

const users: User[] = [
  { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Admin' },
  { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'Editor' },
];

<DataTable columns={columns} data={users} />`;

const stripedTableCode = `<DataTable 
  columns={columns} 
  data={users} 
  striped 
/>`;

const stripedColumnsCode = `<DataTable 
  columns={columns} 
  data={users} 
  stripedColumns 
/>`;

const borderedTableCode = `<DataTable 
  columns={columns} 
  data={users} 
  bordered 
/>`;

const borderlessTableCode = `<DataTable 
  columns={columns} 
  data={users} 
  borderless 
/>`;

const hoverTableCode = `<DataTable 
  columns={columns} 
  data={users} 
  hover 
/>`;

const smallTableCode = `<DataTable 
  columns={columns} 
  data={users} 
  size={DataTableSize.Small}
  bordered
/>`;

const darkHeaderTableCode = `<DataTable 
  columns={columns} 
  data={users} 
  darkHeader 
  striped
/>`;

const colorTableCode = `// Primary color
<DataTable 
  columns={columns} 
  data={users} 
  color={DataTableColor.Primary}
/>

// Success color
<DataTable 
  columns={columns} 
  data={users} 
  color={DataTableColor.Success}
/>

// Dark color
<DataTable 
  columns={columns} 
  data={users} 
  color={DataTableColor.Dark}
/>`;

const alignedTableCode = `const columns: DataTableColumn<User>[] = [
  { header: 'ID', accessor: 'id', align: 'right' },
  { header: 'Name', accessor: 'name', align: 'left' },
  { header: 'Email', accessor: 'email', align: 'center' },
  { header: 'Role', accessor: 'role', align: 'right' },
];

<DataTable columns={columns} data={users} bordered />`;

const customRenderingCode = `const columns: DataTableColumn<User>[] = [
  { header: 'ID', accessor: 'id', width: 80, align: 'center' },
  { header: 'Name', accessor: 'name' },
  { header: 'Email', accessor: 'email' },
  { 
    header: 'Status', 
    accessor: (row) => (
      <Badge color={row.status === 'active' ? BadgeColor.Success : BadgeColor.Secondary}>
        {row.status}
      </Badge>
    ),
    align: 'center'
  },
  { 
    header: 'Actions', 
    accessor: () => (
      <Button color={ButtonColor.Primary} className="btn-sm">
        Edit
      </Button>
    ),
    align: 'center'
  }
];

<DataTable columns={columns} data={users} hover bordered />`;

const clickableRowsCode = `<DataTable 
  columns={columns} 
  data={users} 
  hover
  onRowClick={(row, index) => {
    console.log('Clicked row:', row, 'at index:', index);
    alert(\`Clicked on \${row.name}\`);
  }}
/>`;

const captionTableCode = `<DataTable 
  columns={columns} 
  data={users} 
  caption="List of active users"
  bordered
/>`;

const captionTopTableCode = `<DataTable 
  columns={columns} 
  data={users} 
  caption="List of active users"
  captionTop
  bordered
/>`;

const emptyStateCode = `<DataTable 
  columns={columns} 
  data={[]} 
  emptyMessage="No users found. Please add some users."
  bordered
/>`;

const responsiveTableCode = `// Responsive at all breakpoints (default)
<DataTable columns={columns} data={users} responsive />

// Responsive only below medium breakpoint
<DataTable columns={columns} data={users} responsive="md" />

// Not responsive
<DataTable columns={columns} data={users} responsive={false} />`;
